import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Mail, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const ProSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [subscriptionData, setSubscriptionData] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const verifySubscription = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`${API}/api/pro/success?session_id=${sessionId}`);
        const data = await response.json();
        
        if (data.status === 'success') {
          setSubscriptionData(data);
          setStatus('success');
        } else if (data.status === 'pending') {
          setStatus('pending');
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
      }
    };

    verifySubscription();
  }, [sessionId, API]);

  const planNames = {
    solo: 'Solo',
    pro_desk: 'Pro Desk',
    agency: 'Agency',
    white_label: 'White Label',
    enterprise: 'Enterprise'
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Verifying your subscription...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">!</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h1>
            <p className="text-slate-600 mb-6">
              We couldn't verify your subscription. Please contact us at contact@petflycheck.io
            </p>
            <Link to="/pro">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Return to Plans
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="PetFlyCheck" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
            <div>
              <span className="font-bold text-xl text-slate-900">PetFlyCheck</span>
              <span className="hidden md:inline text-sm text-blue-600 ml-2 font-medium">for Business</span>
            </div>
          </Link>
        </div>
      </header>

      {/* Success Content */}
      <main className="max-w-2xl mx-auto px-4 py-16">
        <Card className="border-0 shadow-xl overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-center text-white">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Welcome to PetFlyCheck Pro!</h1>
            <p className="text-green-100">Your subscription is now active</p>
          </div>

          <CardContent className="p-8">
            {/* Plan Info */}
            {subscriptionData && (
              <div className="bg-blue-50 rounded-xl p-6 mb-8">
                <h3 className="font-bold text-blue-900 mb-4">Subscription Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Plan:</span>
                    <span className="font-semibold text-slate-900">
                      {planNames[subscriptionData.plan] || subscriptionData.plan}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Email:</span>
                    <span className="font-semibold text-slate-900">{subscriptionData.email}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Email Notification */}
            <div className="flex items-start gap-4 bg-amber-50 rounded-xl p-6 mb-8">
              <Mail className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-900 mb-1">Check Your Inbox</h4>
                <p className="text-amber-800 text-sm">
                  We've sent you a welcome email with your account setup instructions. 
                  You'll receive your dashboard access within <strong>24 hours</strong>.
                </p>
              </div>
            </div>

            {/* Next Steps */}
            <h3 className="font-bold text-slate-900 mb-4">What Happens Next?</h3>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Account Setup (Within 24h)</h4>
                  <p className="text-slate-600 text-sm">Our team will create your dashboard and send login credentials to your email.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Customize Your Profile</h4>
                  <p className="text-slate-600 text-sm">Add your company logo, set up team members, and configure branding for PDF reports.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Start Pre-Checking</h4>
                  <p className="text-slate-600 text-sm">Run your first IATA compliance check and generate professional reports for your clients.</p>
                </div>
              </div>
            </div>

            {/* Support Info */}
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <p className="text-slate-600 text-sm mb-2">Need help? We're here for you!</p>
              <a href="mailto:contact@petflycheck.io" className="text-blue-600 font-medium hover:underline">
                contact@petflycheck.io
              </a>
              <p className="text-slate-500 text-xs mt-1">Response within 24 hours guaranteed</p>
            </div>

            {/* CTA */}
            <div className="mt-8 text-center">
              <Link to="/">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Return to Homepage
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProSuccessPage;
